import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { templatesApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = {
  name: "", description: "",
  mission: "", goals: "", scope: "", instructions: "", deliverables: "",
  default_status: "Planning", default_priority: "Medium", default_phase: "",
  default_currency: "USD", default_duration_days: "",
  tasks: [], events: [], notes: [],
};

export default function TemplateEditor({ open, setOpen, template, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(template ? { ...empty, ...template } : empty);
  }, [template, open]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const upd = (kind, idx, patch) => setForm(f => ({ ...f, [kind]: f[kind].map((row, i) => i === idx ? { ...row, ...patch } : row) }));
  const del = (kind, idx) => setForm(f => ({ ...f, [kind]: f[kind].filter((_, i) => i !== idx) }));
  const add = (kind) => setForm(f => {
    if (kind === "tasks") return { ...f, tasks: [...f.tasks, { title: "", description: "", priority: "Normal", status: "Not started", due_offset_days: (f.tasks.length + 1) * 3, order: f.tasks.length }] };
    if (kind === "events") return { ...f, events: [...f.events, { title: "", event_type: "milestone", start_offset_days: 7 }] };
    if (kind === "notes") return { ...f, notes: [...f.notes, { title: "", content: "", entry_type: "note", offset_days: 0 }] };
    return f;
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        default_duration_days: form.default_duration_days ? Number(form.default_duration_days) : null,
        tasks: form.tasks.map((t, i) => ({ ...t, order: i, due_offset_days: t.due_offset_days === "" || t.due_offset_days == null ? null : Number(t.due_offset_days) })),
        events: form.events.map(e => ({
          ...e,
          start_offset_days: e.start_offset_days === "" || e.start_offset_days == null ? null : Number(e.start_offset_days),
          end_offset_days: e.end_offset_days === "" || e.end_offset_days == null ? null : Number(e.end_offset_days),
        })),
        notes: form.notes.map(n => ({ ...n, offset_days: n.offset_days === "" || n.offset_days == null ? null : Number(n.offset_days) })),
      };
      if (template?.id) await templatesApi.update(template.id, payload);
      else await templatesApi.create(payload);
      toast.success("Template saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const L = "text-white/60 text-xs uppercase tracking-wider";
  const I = "mt-1.5 bg-white/5 border-white/10";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#151A23] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{template?.id ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className={L}>Template name</Label><Input data-testid="tpl-name-input" value={form.name} onChange={e=>set("name", e.target.value)} className={I}/></div>
            <div className="sm:col-span-2"><Label className={L}>Description / purpose</Label><Textarea rows={2} value={form.description} onChange={e=>set("description", e.target.value)} className={I}/></div>
          </div>

          <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
            <div className="text-eyebrow mb-3">Executive briefing (carried into every instantiated project)</div>
            <div className="space-y-3">
              <div><Label className={L}>Mission</Label><Textarea rows={2} value={form.mission} onChange={e=>set("mission", e.target.value)} className={I} data-testid="tpl-mission"/></div>
              <div><Label className={L}>Goals / desired outcomes</Label><Textarea rows={2} value={form.goals} onChange={e=>set("goals", e.target.value)} className={I} data-testid="tpl-goals"/></div>
              <div><Label className={L}>Scope</Label><Textarea rows={2} value={form.scope} onChange={e=>set("scope", e.target.value)} className={I} data-testid="tpl-scope"/></div>
              <div><Label className={L}>Instructions / context</Label><Textarea rows={2} value={form.instructions} onChange={e=>set("instructions", e.target.value)} className={I} data-testid="tpl-instructions"/></div>
              <div><Label className={L}>Expected deliverables</Label><Textarea rows={2} value={form.deliverables} onChange={e=>set("deliverables", e.target.value)} className={I} data-testid="tpl-deliverables"/></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className={L}>Default status</Label>
              <Select value={form.default_status} onValueChange={v=>set("default_status", v)}>
                <SelectTrigger className={I}><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#151A23] border-white/10 text-white">{["Planning","Active","On Hold","Completed"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className={L}>Default priority</Label>
              <Select value={form.default_priority} onValueChange={v=>set("default_priority", v)}>
                <SelectTrigger className={I}><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#151A23] border-white/10 text-white">{["Low","Medium","High","Critical"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className={L}>Default phase</Label><Input value={form.default_phase || ""} onChange={e=>set("default_phase", e.target.value)} className={I}/></div>
            <div><Label className={L}>Default currency</Label><Input value={form.default_currency || "USD"} onChange={e=>set("default_currency", e.target.value.toUpperCase())} className={I}/></div>
            <div><Label className={L}>Default duration (days, optional)</Label><Input type="number" min="0" value={form.default_duration_days} onChange={e=>set("default_duration_days", e.target.value)} className={I}/></div>
          </div>

          {/* Tasks */}
          <SectionRepeater
            title="Template tasks"
            testid="tpl-tasks"
            items={form.tasks}
            onAdd={() => add("tasks")}
            render={(t, i) => (
              <div className="grid grid-cols-12 gap-2 items-start" data-testid={`tpl-task-${i}`}>
                <div className="col-span-12 sm:col-span-5"><Input placeholder="Task title" value={t.title} onChange={e=>upd("tasks", i, { title: e.target.value })} className="bg-white/5 border-white/10 h-9 text-sm"/></div>
                <div className="col-span-5 sm:col-span-3">
                  <Select value={t.priority || "Normal"} onValueChange={v=>upd("tasks", i, { priority: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 h-9 text-sm"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-white/10 text-white">{["Low","Normal","Medium","High","Critical"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-5 sm:col-span-3">
                  <Input type="number" placeholder="Due offset (days)" value={t.due_offset_days ?? ""} onChange={e=>upd("tasks", i, { due_offset_days: e.target.value })} className="bg-white/5 border-white/10 h-9 text-sm"/>
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button type="button" onClick={() => del("tasks", i)} className="p-2 rounded hover:bg-red-500/10 text-red-400" data-testid={`tpl-task-remove-${i}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            )}
          />

          {/* Events / Milestones */}
          <SectionRepeater
            title="Template milestones / events"
            testid="tpl-events"
            items={form.events}
            onAdd={() => add("events")}
            render={(ev, i) => (
              <div className="grid grid-cols-12 gap-2 items-start" data-testid={`tpl-event-${i}`}>
                <div className="col-span-12 sm:col-span-6"><Input placeholder="Milestone title" value={ev.title} onChange={e=>upd("events", i, { title: e.target.value })} className="bg-white/5 border-white/10 h-9 text-sm"/></div>
                <div className="col-span-6 sm:col-span-3">
                  <Select value={ev.event_type || "milestone"} onValueChange={v=>upd("events", i, { event_type: v })}>
                    <SelectTrigger className="bg-white/5 border-white/10 h-9 text-sm"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-[#151A23] border-white/10 text-white">{["meeting","deadline","launch","production","payment","followup","milestone"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Input type="number" placeholder="Offset (days)" value={ev.start_offset_days ?? ""} onChange={e=>upd("events", i, { start_offset_days: e.target.value })} className="bg-white/5 border-white/10 h-9 text-sm"/>
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <button type="button" onClick={() => del("events", i)} className="p-2 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            )}
          />

          {/* Notes */}
          <SectionRepeater
            title="Template notes / checklists"
            testid="tpl-notes"
            items={form.notes}
            onAdd={() => add("notes")}
            render={(n, i) => (
              <div className="grid grid-cols-12 gap-2 items-start" data-testid={`tpl-note-${i}`}>
                <div className="col-span-12 sm:col-span-6"><Input placeholder="Note title" value={n.title} onChange={e=>upd("notes", i, { title: e.target.value })} className="bg-white/5 border-white/10 h-9 text-sm"/></div>
                <div className="col-span-12 sm:col-span-5"><Input placeholder="Content" value={n.content} onChange={e=>upd("notes", i, { content: e.target.value })} className="bg-white/5 border-white/10 h-9 text-sm"/></div>
                <div className="col-span-12 sm:col-span-1 flex justify-end">
                  <button type="button" onClick={() => del("notes", i)} className="p-2 rounded hover:bg-red-500/10 text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            )}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)} className="text-white/70 hover:text-white hover:bg-white/10">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tpl-save-btn" className="rounded-full bg-white text-black hover:bg-white/90">{saving?"Saving…":"Save template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectionRepeater({ title, items, onAdd, render, testid }) {
  return (
    <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-eyebrow">{title}</div>
        <Button size="sm" variant="ghost" onClick={onAdd} className="h-7 text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid={`${testid}-add`}>
          <Plus className="w-3.5 h-3.5 mr-1"/> Add
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-white/40 italic">None yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((row, i) => <div key={i}>{render(row, i)}</div>)}
        </div>
      )}
    </div>
  );
}
